/**
 * MoveThreadsModal — move one or more threads to a milestone that has not posted yet (Matt,
 * 2026-10-26; docs/PASSENGERS.md). The crew does this at their own discretion; no tower request.
 *
 * Opens via CustomEvent('open-move-threads-modal', { detail }), host mounted once in OutlineView:
 *   projectId        — the project (required)
 *   threadId         — optional; the thread whose ⋯ menu opened it (ticked to start)
 *   fromMilestoneId  — optional; list every thread riding this milestone (a milestone's Threads ⋯)
 *
 * With only a threadId, the window asks what that thread rides and lists that milestone's other
 * threads too, so several can be ticked and moved in one go. A thread riding nothing is listed alone.
 * Moving a thread also sets its Phase to the new milestone's phase (server side, pairThread).
 */

import { useState, useEffect } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getRidingFor, getPassengersFor, getCandidateMilestones, crewMovePassenger, scheduleOrderedOptions } from '../services/passengers.js'
import { getThreadById } from '../services/threads.js'
import Select from './Select.jsx'
import { refreshMsPassengers } from './MilestoneSections.jsx'

const font = { fontFamily: '"DM Sans",sans-serif' }

export default function MoveThreadsModal({ projectId, threadId, fromMilestoneId, onClose }) {
  const [from, setFrom] = useState(null)          // { milestoneId, milestoneName } | false (riding nothing)
  const [threads, setThreads] = useState(null)    // [{ threadId, title }]
  const [ticked, setTicked] = useState(() => new Set(threadId ? [threadId] : []))
  const [cands, setCands] = useState(null)
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')

  useEffect(() => {
    let dead = false
    ;(async () => {
      try {
        let msId = fromMilestoneId || null, msName = null
        if (!msId && threadId) {
          const r = await getRidingFor([threadId])
          const ride = (r.byThread || {})[threadId]
          if (ride) { msId = ride.milestoneId; msName = ride.milestoneName }
        }
        let list = []
        if (msId) {
          const p = await getPassengersFor([msId])
          list = ((p.byMilestone || {})[msId] || []).map(x => ({ threadId: x.threadId, title: x.title }))
          const pr = ((p.byMilestone || {})[msId] || [])[0]
          if (!msName) msName = (pr && pr.milestoneName) || null
        }
        if (threadId && !list.some(x => x.threadId === threadId)) {
          const t = await getThreadById(threadId)
          list.unshift({ threadId, title: (t && t.fields && t.fields['Title']) || 'Thread' })
        }
        if (!dead) { setFrom(msId ? { milestoneId: msId, milestoneName: msName } : false); setThreads(list) }
        const c = await getCandidateMilestones(projectId)
        if (!dead) setCands(((c.candidates || {})[projectId] || []).filter(x => x.milestoneId !== msId))
      } catch (e) { if (!dead) { setErr(String(e && e.message || e)); setThreads(t => t || []); setCands(c => c || []) } }
    })()
    return () => { dead = true }
  }, [projectId, threadId, fromMilestoneId])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, busy])

  const target = (cands || []).find(c => c.milestoneId === to)
  const options = scheduleOrderedOptions(cands, ctx.state && ctx.state.phases, ctx.state && ctx.state.milestones)

  async function commit() {
    if (!to || !ticked.size) return
    setBusy(true); setErr('')
    const memberId = wf.currentMemberId || null
    const memberName = (ctx.currentMember && ctx.currentMember.fields && (ctx.currentMember.fields['Member Name'] || ctx.currentMember.fields['Name'])) || null
    const ids = [...ticked]
    const moved = []
    try {
      for (const id of ids) {
        await crewMovePassenger(memberId, memberName, id, to, from && from.milestoneName ? `Moved off ${from.milestoneName}` : 'Moved to a milestone')
        moved.push(id)
      }
    } catch (e) { setErr(String(e && e.message || e)) }
    // The riding lists on both milestones, and each moved thread's phase in the project cache.
    refreshMsPassengers([from && from.milestoneId, to])
    if (moved.length) {
      try {
        const r = await getRidingFor(moved)
        for (const t of (Array.isArray(ctx.threadCache) ? ctx.threadCache : [])) {
          const ride = (r.byThread || {})[t.id]
          if (ride && t.fields) { t.fields['Milestone'] = [ride.milestoneId]; if (ride.phaseId) t.fields['Phase'] = [ride.phaseId] }
        }
      } catch (_) { /* the next load picks it up */ }
    }
    window.dispatchEvent(new Event('outline-render'))
    setBusy(false)
    if (moved.length === ids.length) {
      setDone(`${moved.length === 1 ? 'Moved' : moved.length + ' threads moved'} to ${target ? target.name : 'the milestone'}.`)
      setTimeout(onClose, 1100)
    }
  }

  const loading = threads === null
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="MoveThreadsModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 620, maxWidth: 'calc(100vw - 32px)', padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ ...font, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Move to a milestone</div>
          <button onClick={onClose} disabled={busy} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ ...font, fontSize: 12, color: 'var(--aj-text-mid)', marginBottom: 14, lineHeight: 1.5 }}>
          {from && from.milestoneName ? <>Riding <b style={{ fontWeight: 600, color: 'var(--aj-text)' }}>{from.milestoneName}</b> now. </> : from === false ? 'Not riding a milestone yet. ' : ''}
          The thread rides the milestone you pick, and its phase becomes that milestone's phase.
        </div>

        {loading ? (
          <div style={{ ...font, fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 14 }}>Loading threads…</div>
        ) : (
          <>
            {threads.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ ...font, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' }}>Threads to move</span>
                <button style={{ ...font, fontSize: 12, padding: 0, border: 'none', background: 'none', color: 'var(--aj-accent)', cursor: 'pointer' }}
                  onClick={() => setTicked(ticked.size === threads.length ? new Set() : new Set(threads.map(t => t.threadId)))}>{ticked.size === threads.length ? 'Untick all' : 'Tick all'}</button>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {threads.map(t => (
                <label key={t.threadId} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 6, border: '0.5px solid ' + (ticked.has(t.threadId) ? 'var(--aj-accent)' : 'var(--aj-border)'), background: 'var(--aj-bg-subtle)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={ticked.has(t.threadId)} style={{ marginTop: 2 }} disabled={busy}
                    onChange={e => setTicked(prev => { const n = new Set(prev); if (e.target.checked) n.add(t.threadId); else n.delete(t.threadId); return n })} />
                  <span style={{ ...font, fontSize: 13, color: 'var(--aj-text)' }}>{t.title}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <div style={{ ...font, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 6 }}>Move to</div>
        <Select value={to} onChange={setTo} options={options} disabled={busy || cands === null} searchable
          placeholder={cands === null ? 'Loading milestones…' : options.length ? 'Pick a milestone that has not posted…' : 'No other open milestone on this project'}
          style={{ width: '100%', fontSize: 12 }} />

        {err && <div style={{ ...font, fontSize: 12, color: '#c0392b', marginTop: 10 }}>{err}</div>}
        {done && <div style={{ ...font, fontSize: 12, color: '#1D9E75', marginTop: 10 }}>{done}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={busy} style={{ ...font, fontSize: 12, padding: '7px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={commit} disabled={busy || !to || !ticked.size || !!done}
            style={{ ...font, fontSize: 12, padding: '7px 14px', borderRadius: 4, border: 'none', background: (to && ticked.size) ? '#07272D' : 'var(--aj-border-mid)', color: '#fff', cursor: (to && ticked.size) ? 'pointer' : 'default' }}>
            {busy ? 'Moving…' : ticked.size > 1 ? `Move ${ticked.size} threads` : 'Move thread'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Mounted once in OutlineView. */
export function MoveThreadsModalHost() {
  const [cfg, setCfg] = useState(null)
  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      setCfg({ projectId: d.projectId || ctx.recordId || wf.zoomProjectId || '', threadId: d.threadId || null, fromMilestoneId: d.fromMilestoneId || null, n: Date.now() })
    }
    window.addEventListener('open-move-threads-modal', onOpen)
    return () => window.removeEventListener('open-move-threads-modal', onOpen)
  }, [])
  if (!cfg || !cfg.projectId) return null
  return <MoveThreadsModal key={cfg.n} projectId={cfg.projectId} threadId={cfg.threadId} fromMilestoneId={cfg.fromMilestoneId} onClose={() => setCfg(null)} />
}
